import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Unplug,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { mercadopagoKeys } from "@/lib/queryKeys";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  disconnectMp,
  dismissMpError,
  getMpStatus,
  listMpErrors,
  retryMpError,
  startMpOAuth,
  updateMpSettings,
  type MpError,
} from "@/services/mercadopagoService";

export function SettingsMercadopagoIntegrationCard(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  // Surface OAuth callback outcomes from redirect query.
  useEffect(() => {
    const flag = searchParams.get("mp");
    if (!flag) return;
    if (flag === "connected") {
      toast.success(t("settings.integrations.mercadopago.connectSuccess"));
    } else if (flag === "error") {
      const reason = searchParams.get("description") ?? searchParams.get("reason");
      toast.error(
        reason
          ? `${t("settings.integrations.mercadopago.connectError")}: ${reason}`
          : t("settings.integrations.mercadopago.connectError"),
      );
    } else if (flag === "reconnect") {
      toast.error(t("settings.integrations.mercadopago.stateErrorHint"));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("mp");
    next.delete("reason");
    next.delete("description");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: mercadopagoKeys.status(),
    queryFn: getMpStatus,
  });
  const isConnected = status?.status === "active";

  const { data: errors = [], isLoading: errorsLoading } = useQuery({
    queryKey: mercadopagoKeys.errors(),
    queryFn: listMpErrors,
    enabled: isConnected,
  });

  const authorizeMutation = useMutation({
    mutationFn: startMpOAuth,
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
    onError: () => toast.error(t("settings.integrations.mercadopago.connectError")),
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectMp,
    onSuccess: async () => {
      toast.success(t("settings.integrations.mercadopago.disconnectSuccess"));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: mercadopagoKeys.status() }),
        queryClient.invalidateQueries({ queryKey: mercadopagoKeys.errors() }),
      ]);
    },
    onError: () =>
      toast.error(t("settings.integrations.mercadopago.disconnectError")),
  });

  const settingsMutation = useMutation({
    mutationFn: updateMpSettings,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mercadopagoKeys.status() });
    },
    onError: () =>
      toast.error(t("settings.integrations.mercadopago.saveError")),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="text-foreground">
                {t("settings.integrations.mercadopago.title")}
              </CardTitle>
              <CardDescription>
                {t("settings.integrations.mercadopago.description")}
              </CardDescription>
            </div>
            <StateBadge
              status={status?.status ?? "not_connected"}
              loading={statusLoading}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {status?.status === "error" && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <div className="space-y-1">
                <p className="font-medium">
                  {t("settings.integrations.mercadopago.stateError")}
                </p>
                <p>{t("settings.integrations.mercadopago.stateErrorHint")}</p>
                {status.lastError && (
                  <p className="text-xs text-destructive/80">{status.lastError}</p>
                )}
              </div>
            </div>
          )}

          {isConnected && status?.mpUserEmail && (
            <p className="text-sm text-muted-foreground">
              {t("settings.integrations.mercadopago.connectedAs", {
                email: status.mpUserEmail,
              })}
            </p>
          )}

          {isConnected && status?.mpSiteId && status.mpSiteId !== "MLU" && (
            <div className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
              <AlertCircle className="size-4 shrink-0" aria-hidden />
              {t("settings.integrations.mercadopago.siteNotUy", {
                site: status.mpSiteId,
              })}
            </div>
          )}

          {isConnected && (
            <div className="flex items-start gap-2">
              <Checkbox
                id="mp-auto-email"
                checked={status.autoEmailBuyer}
                disabled={settingsMutation.isPending}
                onCheckedChange={(value) =>
                  settingsMutation.mutate({ autoEmailBuyer: value === true })
                }
              />
              <div className="space-y-1">
                <Label
                  htmlFor="mp-auto-email"
                  className="cursor-pointer text-sm font-medium"
                >
                  {t("settings.integrations.mercadopago.autoEmailBuyerLabel")}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("settings.integrations.mercadopago.autoEmailBuyerHint")}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {status?.status !== "active" && (
              <Button
                type="button"
                onClick={() => authorizeMutation.mutate()}
                disabled={authorizeMutation.isPending}
              >
                {authorizeMutation.isPending ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : null}
                {status?.status === "error"
                  ? t("settings.integrations.mercadopago.reconnectButton")
                  : t("settings.integrations.mercadopago.connectButton")}
              </Button>
            )}
            {isConnected && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDisconnectOpen(true)}
                disabled={disconnectMutation.isPending}
              >
                <Unplug className="mr-2 size-4" />
                {t("settings.integrations.mercadopago.disconnectButton")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {isConnected && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <CardTitle className="text-base text-foreground">
                  {t("settings.integrations.mercadopago.errorsTitle")}
                </CardTitle>
                <CardDescription>
                  {t("settings.integrations.mercadopago.errorsHelp")}
                </CardDescription>
              </div>
              {status?.unresolvedErrorCount !== undefined &&
                status.unresolvedErrorCount > 0 && (
                  <StatusBadge variant="danger">
                    {status.unresolvedErrorCount}
                  </StatusBadge>
                )}
            </div>
          </CardHeader>
          <CardContent>
            {errorsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                <span>{t("settings.integrations.mercadopago.loadingErrors")}</span>
              </div>
            ) : errors.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="size-4 text-emerald-600" />
                {t("settings.integrations.mercadopago.noErrors")}
              </div>
            ) : (
              <MpErrorsTable errors={errors} />
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmationDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title={t("settings.integrations.mercadopago.disconnectButton")}
        description={t("settings.integrations.mercadopago.disconnectConfirm")}
        confirmText={t("settings.integrations.mercadopago.disconnectButton")}
        variant="destructive"
        isLoading={disconnectMutation.isPending}
        onConfirm={async () => {
          await disconnectMutation.mutateAsync();
          setDisconnectOpen(false);
        }}
      />
    </div>
  );
}

function StateBadge({
  status,
  loading,
}: {
  status: "active" | "disconnected" | "error" | "not_connected";
  loading: boolean;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
      </div>
    );
  }
  if (status === "active") {
    return (
      <StatusBadge variant="success">
        {t("settings.integrations.mercadopago.stateConnected")}
      </StatusBadge>
    );
  }
  if (status === "error") {
    return (
      <StatusBadge variant="danger">
        {t("settings.integrations.mercadopago.stateError")}
      </StatusBadge>
    );
  }
  if (status === "disconnected") {
    return (
      <StatusBadge variant="neutral">
        {t("settings.integrations.mercadopago.stateDisconnected")}
      </StatusBadge>
    );
  }
  return (
    <StatusBadge variant="neutral">
      {t("settings.integrations.mercadopago.stateNotConnected")}
    </StatusBadge>
  );
}

function MpErrorsTable({ errors }: { errors: MpError[] }): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const retryMutation = useMutation({
    mutationFn: (id: string) => retryMpError(id),
    onSuccess: async (result) => {
      const outcomeKey =
        result.outcome === "emitted" || result.outcome === "already_emitted"
          ? "settings.integrations.mercadopago.retrySuccess"
          : "settings.integrations.mercadopago.retryQueued";
      toast.success(t(outcomeKey));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: mercadopagoKeys.errors() }),
        queryClient.invalidateQueries({ queryKey: mercadopagoKeys.status() }),
      ]);
    },
    onError: () =>
      toast.error(t("settings.integrations.mercadopago.retryError")),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => dismissMpError(id),
    onSuccess: async () => {
      toast.success(t("settings.integrations.mercadopago.dismissSuccess"));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: mercadopagoKeys.errors() }),
        queryClient.invalidateQueries({ queryKey: mercadopagoKeys.status() }),
      ]);
    },
    onError: () =>
      toast.error(t("settings.integrations.mercadopago.dismissError")),
  });

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("settings.integrations.mercadopago.columnPayment")}</TableHead>
          <TableHead>{t("settings.integrations.mercadopago.columnReason")}</TableHead>
          <TableHead className="text-center">
            {t("settings.integrations.mercadopago.columnAttempts")}
          </TableHead>
          <TableHead>{t("settings.integrations.mercadopago.columnLastAttempt")}</TableHead>
          <TableHead className="text-right">
            {t("settings.integrations.mercadopago.columnActions")}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {errors.map((e) => {
          const pending =
            (retryMutation.isPending && retryMutation.variables === e.id) ||
            (dismissMutation.isPending && dismissMutation.variables === e.id);
          return (
            <TableRow key={e.id}>
              <TableCell>
                <div className="space-y-1">
                  <div className="font-mono text-xs">{e.mpPaymentId}</div>
                  <div className="text-xs text-muted-foreground">
                    {t(`settings.integrations.mercadopago.errorCode.${e.errorCode}`, {
                      defaultValue: e.errorCode,
                    })}
                  </div>
                </div>
              </TableCell>
              <TableCell className="max-w-md text-sm">{e.errorMessage}</TableCell>
              <TableCell className="text-center text-sm">
                {e.attemptCount}
              </TableCell>
              <TableCell className="text-sm">
                {formatDisplayDate(e.lastAttemptAt)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => retryMutation.mutate(e.id)}
                    disabled={pending}
                  >
                    {pending && retryMutation.variables === e.id ? (
                      <Loader2 className="mr-1 size-3 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-1 size-3" />
                    )}
                    {t("settings.integrations.mercadopago.retry")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => dismissMutation.mutate(e.id)}
                    disabled={pending}
                  >
                    <X className="size-3" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
